/**
 * Route recommendation engine — the "Pegasus recommends" brain. Given a tech's
 * stops and the rest of the board (each tech's stops + skills), it produces a
 * ranked list of plain-English suggestions the dispatcher can read and apply:
 *   - reorder this tech's day for a shorter drive,
 *   - hand a movable call to a *qualified* nearby tech (with the why),
 *   - flag a call the assigned tech isn't skilled for.
 *
 * Numbers come from the route math (route.ts); the engine never invents them.
 * Skill safety: a call is only suggested for a tech who has every skill it
 * needs. Call skill needs are derived from the notes (the same signal the
 * historical revealed-skills analysis used).
 */
import {
  type LatLng,
  routeMiles,
  optimizeBanded,
  insertionCostMiles,
  driveMinutes,
} from "./route";

/** "(Eagan) " for a stop with a city, else "" — for the move phrasing. */
function whereTag(stop: { city?: string | null }): string {
  return stop.city ? ` (${stop.city})` : "";
}

/**
 * True when the target tech ALREADY carries this very dispatch — a multitech
 * (2-man) job puts the same dispatch on both techs' boards. "Moving" it to a
 * tech who's already on it is meaningless, and the preview double-counted the
 * stop. Such targets are skipped everywhere. (AUDIT-2026-06-09 A2.)
 */
function alreadyOn(target: RecoTech, stop: RecoStop): boolean {
  return target.stops.some((s) => s.dispatch === stop.dispatch);
}

export interface RecoStop extends LatLng {
  dispatch: string;
  label: string;
  band: number; // time band (for window-aware ordering)
  requiredSkills: number[]; // skill_domain ids this call needs (may be empty)
  // Soft-lock signals from the notes (dispatch + location). A call the customer
  // asked a specific tech for, or that needs a physical attribute, must never be
  // auto-suggested for a move away (and if it's on the WRONG tech, the right
  // move is TO the requested one). See deriveRequestedTech / deriveAttrLock.
  city?: string | null; // for the "Move <name> (<city>) to <tech>" phrasing
  requestedEmpno?: string | null;
  requestedEmpnos?: string[]; // ALL acceptable techs ("Jeremy or Camron") — the call is on the right tech if it's on ANY of these
  requestedName?: string | null; // the requested tech's name (for off-board flags)
  excludedEmpnos?: string[]; // techs the notes say to AVOID ("no Miles" / "anyone but Miles")
  attrLock?: boolean;
  // Optional display fields so a Pegasus-native stop renders its time/window in
  // the MovePreview even though it has no ESC `Dispatch` row to look them up from.
  clock?: string | null; // formatted arrival time
  win?: string | null; // arrival-window label
}

export interface RecoTech {
  empno: string;
  name: string;
  skills: Set<number>; // skill_domain ids the tech is flagged capable of
  stops: RecoStop[];
}

export type Recommendation =
  | {
      kind: "reorder";
      techEmpno: string;
      savingMiles: number;
      text: string;
    }
  | {
      kind: "move";
      techEmpno: string;
      dispatch: string;
      label: string;
      toEmpno: string;
      toName: string;
      savingMiles: number;
      text: string;
      why: string;
      requested?: boolean; // customer asked for this tech → top priority
    }
  | {
      kind: "flag";
      techEmpno: string;
      dispatch: string;
      label: string;
      text: string;
    };

// notes keyword → skill_domain id (matches the seeded skill_domain rows).
const SKILL_KEYWORDS: { id: number; name: string; re: RegExp }[] = [
  { id: 1, name: "Boiler", re: /boiler/i },
  { id: 9, name: "Mini-Split", re: /mini[\s-]?split|ductless/i },
  { id: 8, name: "Magic-Pak", re: /magic\s?-?pak/i },
  { id: 5, name: "Gas Line", re: /gas line|gas leak/i },
  { id: 6, name: "In-Floor Heat", re: /in[\s-]?floor|radiant/i },
  { id: 15, name: "Zone System", re: /zone/i },
  { id: 2, name: "Commercial", re: /commercial|rooftop|\brtu\b/i },
];

const SKILL_NAME = new Map(SKILL_KEYWORDS.map((k) => [k.id, k.name]));

/** Human label for a skill_domain id (for badges / flags). */
export function skillName(id: number): string {
  return SKILL_NAME.get(id) ?? `skill ${id}`;
}

/** Skill ids a call needs, inferred from its notes (empty = any tech). */
export function deriveRequiredSkills(notes: string | null | undefined): number[] {
  if (!notes) return [];
  return SKILL_KEYWORDS.filter((k) => k.re.test(notes)).map((k) => k.id);
}

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Detect a customer-requested tech from the notes (dispatch notes + location
 * notes). Seth's patterns: location notes like "Miles Only" / "Customer prefers
 * Miles"; a dispatch that names the tech. We require a request KEYWORD next to
 * the name ("only / prefers / requests / send / wants / ask for") so a bare
 * distance like "5 miles out" can't masquerade as a request for tech Miles —
 * conservative on purpose (a false "move to X" is worse than a missed lock). A
 * bare standalone tech name with no keyword is left for the MIG-NOTES-01 AI pass.
 * Returns the requested tech's empno, or null.
 */
// Words that flip a request into a NON-request when they sit right before the
// matched phrase: "do NOT send Camron", "does NOT want Miles", "anyone but X".
const NEGATION =
  /^(?:not|no|never|none|nor|but|cannot|can'?t|do?n'?t|does(?:n'?t)?|did(?:n'?t)?|won'?t|wo?nt|isn'?t|aren'?t|wasn'?t)$/i;

/**
 * ALL techs the customer would accept, from the notes. Handles "A or B" / "A and
 * B" / "A / B" alternates that follow a request keyword (location note "prefers
 * Jeremy or Camron" → BOTH), so a call placed on EITHER acceptable tech is not a
 * conflict. Negation-aware. Returns deduped empnos.
 */
export function deriveRequestedTechs(
  text: string | null | undefined,
  roster: { empno: string; first: string | null | undefined }[],
): string[] {
  if (!text) return [];
  const byFirst = new Map<string, string>();
  for (const t of roster) {
    const f = (t.first ?? "").trim();
    if (f.length >= 3) byFirst.set(f.toLowerCase(), t.empno);
  }
  const set = new Set<string>();
  for (const t of roster) {
    const f = (t.first ?? "").trim();
    if (f.length < 3) continue; // too short to match safely
    const n = escapeRegex(f);
    const re = new RegExp(
      `\\b(?:${n}\\s+only|${n}\\s+request(?:ed|s)?|request(?:ed|s)?\\s+${n}|prefers?\\s+${n}|send\\s+${n}|wants?\\s+${n}|ask(?:ed|s)?\\s+for\\s+${n})\\b`,
      "gi",
    );
    let m: RegExpExecArray | null;
    while ((m = re.exec(text)) !== null) {
      // Reject a mention negated by the word right before it — the whole "Do not
      // send Camron" class (a false "move to X" is worse than a missed request).
      const before = text.slice(Math.max(0, m.index - 16), m.index);
      const prevWord = (before.match(/([A-Za-z']+)[\s,]*$/) || [])[1] ?? "";
      if (NEGATION.test(prevWord)) continue;
      set.add(t.empno);
      // Chain any trailing alternates ("… Jeremy or Camron", "Camron / Jeremy").
      let rest = text.slice(m.index + m[0].length, m.index + m[0].length + 48);
      let cm: RegExpMatchArray | null;
      while ((cm = rest.match(/^\s*(?:[,/]|\bor\b|\band\b)\s*([A-Za-z']{3,})/i)) !== null) {
        const emp = byFirst.get(cm[1].toLowerCase());
        if (emp) set.add(emp);
        rest = rest.slice(cm[0].length);
        if (!emp) break; // stop at the first token that isn't a known tech
      }
    }
  }
  return [...set];
}

/**
 * The PRIMARY requested tech (back-compat single value). When `assignedEmpno`
 * is supplied, returns null if that tech is ALREADY one the customer would
 * accept — so a call on an acceptable alternate ("Jeremy or Camron", placed on
 * Camron) is never flagged as the "wrong tech".
 */
export function deriveRequestedTech(
  text: string | null | undefined,
  roster: { empno: string; first: string | null | undefined }[],
  assignedEmpno?: string | null,
): string | null {
  const set = deriveRequestedTechs(text, roster);
  if (assignedEmpno && set.includes(assignedEmpno)) return null;
  return set[0] ?? null;
}

/**
 * Detect a physical-attribute requirement ("senior tech", "skinny tech", etc).
 * We can't verify the attribute, so the call is soft-locked: never auto-move it.
 */
export function deriveAttrLock(text: string | null | undefined): boolean {
  if (!text) return false;
  return /\b(senior|skinny|strong|small|young|experienced)\s+tech\b/i.test(text);
}

/**
 * Detect techs the customer does NOT want ("does not care for Miles", "no
 * Miles", "anyone but Miles", "Miles not welcome"). The optimizer must never
 * route the call to an excluded tech, and if it's already ON one, flag it.
 * Keyword-anchored like the request detector to avoid false hits.
 */
export function deriveExcludedTechs(
  text: string | null | undefined,
  roster: { empno: string; first: string | null | undefined }[],
): string[] {
  if (!text) return [];
  const out: string[] = [];
  for (const t of roster) {
    const f = (t.first ?? "").trim();
    if (f.length < 3) continue;
    const n = escapeRegex(f);
    const re = new RegExp(
      `\\b(?:do(?:es)?\\s*n[o']?t\\s+(?:want|like|care\\s+for|send|use)\\s+${n}|anyone\\s+but\\s+${n}|no\\s+${n}\\b|${n}\\s+(?:is\\s+)?not\\s+(?:allowed|welcome|wanted|back))`,
      "i",
    );
    if (re.test(text)) out.push(t.empno);
  }
  return out;
}

function qualified(tech: RecoTech, stop: RecoStop): boolean {
  return stop.requiredSkills.every((s) => tech.skills.has(s));
}

/** Marginal miles a stop adds to / removes from an open route. */
function marginalMiles(route: RecoStop[], stop: RecoStop): number {
  const without = route.filter((s) => s.dispatch !== stop.dispatch);
  return insertionCostMiles(without, stop);
}

/**
 * Total drive saved by moving `stop` from `fromStops` to `toStops`, measured the
 * SAME window-aware way the Review-move preview measures it: each route in its
 * optimized (banded) order, before vs after. So the card's headline number
 * always matches what the preview shows. (The geographic insertion cost is still
 * used to *pick* the best target cheaply; this just measures the chosen move.)
 */
function windowAwareNet(
  fromStops: RecoStop[],
  toStops: RecoStop[],
  stop: RecoStop,
): number {
  const fromBefore = routeMiles(optimizeBanded(fromStops));
  const fromAfter = routeMiles(
    optimizeBanded(fromStops.filter((s) => s.dispatch !== stop.dispatch)),
  );
  const toBefore = routeMiles(optimizeBanded(toStops));
  const toAfter = routeMiles(optimizeBanded([...toStops, stop]));
  return fromBefore + toBefore - (fromAfter + toAfter);
}

export interface RecoOptions {
  minReorderMiles?: number;
  minMoveMiles?: number;
}

/**
 * Ranked recommendations for one tech's day.
 */
export function recommendForTech(
  techEmpno: string,
  techs: RecoTech[],
  opts: RecoOptions = {},
): Recommendation[] {
  const minReorder = opts.minReorderMiles ?? 3;
  const minMove = opts.minMoveMiles ?? 4;
  const tech = techs.find((t) => t.empno === techEmpno);
  if (!tech || tech.stops.length === 0) return [];

  const recos: Recommendation[] = [];
  const others = techs.filter((t) => t.empno !== techEmpno);

  // 1) Reorder — optimized (window-aware) vs the scheduled-time order.
  if (tech.stops.length >= 3) {
    const planned = [...tech.stops].sort((a, b) => a.band - b.band);
    const optimized = optimizeBanded(tech.stops);
    const saving = routeMiles(planned) - routeMiles(optimized);
    if (saving >= minReorder) {
      recos.push({
        kind: "reorder",
        techEmpno,
        savingMiles: saving,
        text: `Re-run ${tech.name}'s route in a tighter order — saves ~${saving.toFixed(1)} mi · ~${Math.round(driveMinutes(saving))} min.`,
      });
    }
  }

  // 2) Cross-tech moves + 3) skill flags, per stop.
  for (const stop of tech.stops) {
    // Skill flag: the assigned tech isn't capable of this call.
    if (!qualified(tech, stop)) {
      const need = stop.requiredSkills
        .map((s) => SKILL_NAME.get(s) ?? `skill ${s}`)
        .join(", ");
      const able = others.filter((o) => qualified(o, stop)).map((o) => o.name);
      recos.push({
        kind: "flag",
        techEmpno,
        dispatch: stop.dispatch,
        label: stop.label,
        text: `${stop.label} needs ${need} — ${tech.name} isn't flagged for it${
          able.length ? `; ${able.join(" or ")} can take it` : ""
        }.`,
      });
    }

    // Requested-tech soft lock. If the customer asked for a SPECIFIC tech:
    //  - and the call is on the WRONG tech → recommend moving it to who they
    //    asked for (top priority; it's a stated customer request),
    //  - and the call is already on the requested tech → never suggest moving it
    //    away. Same for an attribute-locked call (senior/skinny tech).
    // The customer may accept ANY of several techs ("Jeremy or Camron"). Only a
    // wrong-tech situation if the call is on NONE of them.
    const reqSet = stop.requestedEmpnos?.length
      ? stop.requestedEmpnos
      : stop.requestedEmpno
        ? [stop.requestedEmpno]
        : [];
    const onRequested = reqSet.includes(techEmpno);
    if (reqSet.length > 0 && !onRequested) {
      const target = reqSet
        .map((e) => others.find((o) => o.empno === e))
        .find((o): o is RecoTech => !!o);
      if (target && alreadyOn(target, stop)) {
        // Requested tech already shares this multitech job — nothing to move.
      } else if (target) {
        const net = marginalMiles(tech.stops, stop) - insertionCostMiles(target.stops, stop);
        recos.push({
          kind: "move",
          techEmpno,
          dispatch: stop.dispatch,
          label: stop.label,
          toEmpno: target.empno,
          toName: target.name,
          savingMiles: net,
          requested: true,
          text: `Move ${stop.label}${whereTag(stop)} to ${target.name}.`,
          why: qualified(target, stop)
            ? `Customer requested ${target.name} (it's in the notes).`
            : `Customer requested ${target.name}, but he isn't flagged for this call's skill — confirm before moving.`,
        });
      } else {
        // The requested tech isn't on today's board — can't move it to him; flag
        // so the dispatcher knows the call is on someone other than who was asked.
        recos.push({
          kind: "flag",
          techEmpno,
          dispatch: stop.dispatch,
          label: stop.label,
          text: `${stop.label} asked for ${stop.requestedName ?? "a specific tech"}, who isn't on today's board — it's on ${tech.name} for now.`,
        });
      }
      continue; // a requested call doesn't also get a "nearby tech" suggestion
    }
    // Exclusion: the notes say to AVOID this very tech, but the call is on him.
    // Always surface it (constraint violation, regardless of mileage): hand it
    // to the cheapest qualified tech the notes DON'T exclude, or flag if none.
    const excluded = stop.excludedEmpnos ?? [];
    if (excluded.includes(techEmpno)) {
      let alt: { t: RecoTech; cost: number } | null = null;
      for (const o of others) {
        if (!qualified(o, stop) || excluded.includes(o.empno) || alreadyOn(o, stop))
          continue;
        const cost = insertionCostMiles(o.stops, stop);
        if (!alt || cost < alt.cost) alt = { t: o, cost };
      }
      if (alt) {
        const net = marginalMiles(tech.stops, stop) - alt.cost;
        recos.push({
          kind: "move",
          techEmpno,
          dispatch: stop.dispatch,
          label: stop.label,
          toEmpno: alt.t.empno,
          toName: alt.t.name,
          savingMiles: net,
          requested: true, // constraint-driven → rank with the top priorities
          text: `Move ${stop.label}${whereTag(stop)} off ${tech.name} to ${alt.t.name}.`,
          why: `The notes exclude ${tech.name}; ${alt.t.name} is qualified${
            net >= 0.1 ? ` and saves ~${net.toFixed(1)} mi` : ""
          }.`,
        });
      } else {
        recos.push({
          kind: "flag",
          techEmpno,
          dispatch: stop.dispatch,
          label: stop.label,
          text: `${stop.label} — notes ask NOT to send ${tech.name}, but it's on his board and no other qualified tech is free today.`,
        });
      }
      continue;
    }

    if (onRequested || stop.attrLock) continue;

    // Best QUALIFIED other tech to hand this call to (never an excluded one).
    // Pick the target cheaply by geographic insertion cost...
    let best: { t: RecoTech; cost: number } | null = null;
    for (const o of others) {
      if (!qualified(o, stop) || excluded.includes(o.empno) || alreadyOn(o, stop))
        continue;
      const cost = insertionCostMiles(o.stops, stop);
      if (!best || cost < best.cost) best = { t: o, cost };
    }
    if (best) {
      // ...then measure the saving the window-aware way (matches the preview).
      const net = windowAwareNet(tech.stops, best.t.stops, stop);
      if (net >= minMove) {
        const specialty = stop.requiredSkills.length > 0;
        recos.push({
          kind: "move",
          techEmpno,
          dispatch: stop.dispatch,
          label: stop.label,
          toEmpno: best.t.empno,
          toName: best.t.name,
          savingMiles: net,
          text: `Move ${stop.label}${whereTag(stop)} to ${best.t.name} — saves ~${net.toFixed(1)} mi · ~${Math.round(driveMinutes(net))} min.`,
          why: specialty
            ? `${best.t.name} is qualified and already routing nearby.`
            : `Not skill-specific, and ${best.t.name} is already routing nearby.`,
        });
      }
    }
  }

  // Rank: customer-requested moves first, then biggest mileage savings, flags last.
  const score = (r: Recommendation) => {
    if (r.kind === "flag") return -1;
    if (r.kind === "move" && r.requested) return 1e6 + (r.savingMiles ?? 0);
    return r.savingMiles ?? 0;
  };
  recos.sort((a, b) => score(b) - score(a));
  return recos;
}
